import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useGetReportIncomeQuery, useGetRoomFreesQuery, useGetSummaryQuery } from "@/services/dashboard.service";
import Breadcrumb from "@/components/Breadcrumb";
import ECharts from "@/components/Chart/ECharts";
import { useGetDropDownBusinessUnitsQuery } from "@/services/businessunit.service";
import DropDownList from "@/components/Dropdown";
import { DataYears, ItemBusinessUnitAll } from "@/constants/default";
import useColorMode from "@/hook/useColorMode";
import { ModeUI } from "@/constants/enum";
import { useGetTransactionTotalMonthsQuery, useGetTransactionTotalQuery } from "@/services/transaction.service";
import TableComponent from "@/components/Table/TabelComponent";
import TableHeaderCash from "./_headerCash";
import TableHeaderRoomFree from "./_headerRoomFree";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import moment from "moment-timezone";

export default function Dashboard() {
    const {t} = useTranslation()
    const dataYears = DataYears()
    const [businessUnit, setBusinessUnit] = useState<string>("")
    const [selectedYear, setSelectedYear] = useState<string>(dataYears[0].value)
    const { data: dataSummary, refetch } = useGetSummaryQuery({}, {refetchOnMountOrArgChange: true})
    const {data: dataIncome, refetch: refetchIncome, isFetching: isFetchingIncome} = useGetReportIncomeQuery({business_unit_id: businessUnit}, {refetchOnFocus: true, refetchOnMountOrArgChange: true, refetchOnReconnect: true})
    const {data: businessUnits} = useGetDropDownBusinessUnitsQuery({},{ refetchOnMountOrArgChange: true })
    const { data: dataTotal, refetch: refetchTotal, isFetching: isFetchingTotal } = useGetTransactionTotalQuery({year: selectedYear}, {refetchOnMountOrArgChange: true})
    const { data: dataTotalMonths, refetch: refetchTotalMonths, isFetching: isFetchingTotalMonths } = useGetTransactionTotalMonthsQuery(undefined, {refetchOnMountOrArgChange: true})
    const {data: roomFrees, refetch: refetchRoomFrees, isFetching: isFetchingRoomFree} = useGetRoomFreesQuery({}, {refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true})
    const itemBusinessUnit = ItemBusinessUnitAll();
    const tabs = [t("dashboard.finance"), t("dashboard.properties")];
    const [selectedIndex, setSelectedIndex] = useState(0)
    const tabRefs = useRef<(HTMLElement | null)[]>([]);
    const totalDataBalance = dataTotal ? Math.abs(dataTotal.income - dataTotal.expense).toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 2}) : 0
    const [mode, setMode] = useState<ModeUI>(useColorMode().mode)

    const [highlightStyle, setHighlightStyle] = useState({
        width: "0px",
        left: "0px",
    });

    useEffect(() => {
        if (tabRefs.current[selectedIndex]) {
            const tab = tabRefs.current[selectedIndex];
            setHighlightStyle({
                width: `${tab.offsetWidth}px`,
                left: `${tab.offsetLeft}px`,
            });
        }
    }, [selectedIndex, tabRefs])

    useEffect(() => {
        const handler = () => {
            let updated = localStorage.getItem('color-theme');
            if(updated) {
            updated = JSON.parse(updated)
            if(updated === ModeUI.AUTO) {
                updated = window.matchMedia && window.matchMedia("(prefers-color-scheme:dark)").matches ? ModeUI.DARK : ModeUI.LIGHT
            }
            setMode(updated as ModeUI)
            }
        }

        window.addEventListener('local-storage-updated', handler);
        return () => window.removeEventListener('local-storage-updated', handler);
    }, []);

    return (
        <div className="w-full">
            <div className="flex justify-between mb-5 items-end">
                <Breadcrumb pageName={t("dashboard.dashboard")} isVisable={false} />
                <div className="flex justify-start md:justify-end mt-2 md:mt-0">
                    <div className="me-2">
                        <button className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 font-medium text-white text" onClick={() => {
                            refetch()
                            refetchIncome()
                            refetchTotal()
                            refetchTotalMonths()
                        }}><i className="fa-sharp fa-light fa-arrows-rotate me-2 text-white" />{t("table.reload")}</button>
                    </div>
                </div>
            </div>

            <div className="w-full relative">
                <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                    <TabList className="flex justify-center border-b">
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                ref={(el) => (tabRefs.current[index] = el)}
                                className={({ selected }) => `text-gray-500 font-medium px-6 py-2 focus:outline-none relative ${selected ? "text-blue-600 font-semibold" : "hover:text-gray-700"}`}
                            >
                                <h4 className="text-title-md">{tab}</h4>
                                {
                                    selectedIndex === index && (
                                        <span
                                            className="absolute bottom-0 h-1 bg-blue-600 transition-all duration-300 rounded-full"
                                            style={{
                                                width: "100%",
                                                left: 0,
                                            }}
                                        />
                                    )
                                }
                            </Tab>
                        ))}
                        
                    </TabList>
                    <TabPanels className="mt-3">
                        <TabPanel>
                            <div className="flex flex-col gap-10 mt-3">
                                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
                                    <div className="flex">
                                        <DropDownList
                                            selected={selectedYear}
                                            setSelected={setSelectedYear}
                                            list={dataYears}
                                            placeholder="Select year"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6 2xl:gap-7.5 mt-3">
                                        <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                            <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                                <i className="fa-solid fa-sack-dollar fa-lg text-meta-3" />
                                            </div>
                                    
                                            <div className="flex items-center justify-between ms-5">
                                                <div>
                                                    <h4 className="text-title-md font-bold text-meta-3">
                                                        {dataTotal ? dataTotal.income.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 2}) : 0}
                                                    </h4>
                                                    <span className="text-sm font-medium">{t("dashboard.revenue")}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                            <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                                <i className="fa-solid fa-money-check-dollar fa-lg text-red" />
                                            </div>
                                    
                                            <div className="flex items-center justify-between ms-5">
                                                <div>
                                                    <h4 className="text-title-md font-bold text-red">
                                                        {dataTotal ? dataTotal.expense.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 2}) : 0}
                                                    </h4>
                                                    <span className="text-sm font-medium">{t("dashboard.expense")}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                            <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                                <i className="fa-solid fa-coins fa-lg text-meta-5" />
                                            </div>
                                            <div className="flex items-center justify-between ms-5">
                                                <div>
                                                    <h4 className="text-title-md font-bold text-meta-5">
                                                        {dataTotal ? `${dataTotal.income - dataTotal.expense < 0 ? '- ' : ''}${totalDataBalance}` : 0}
                                                    </h4>
                                                    <span className="text-sm font-medium">{t("dashboard.net_income")}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-10 mt-3">
                                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h4 className="text-base font-semibold">{t("dashboard.financial_statement")}</h4>
                                            <p>[{t("dashboard.24_months")}]</p>
                                        </div>
                                        <div className="mb-1">
                                            {
                                                businessUnits && <DropDownList
                                                    selected={businessUnit}
                                                    setSelected={setBusinessUnit}
                                                    list={[itemBusinessUnit, ...businessUnits]}
                                                />
                                            }
                                        </div>
                                    </div>
                                    <div className="w-full mt-3">
                                        {dataIncome && <ECharts
                                                height={600}
                                                xAxis={{
                                                    data: dataIncome?.map(i => moment(i.date, 'MM-YYYY').format('MMM YYYY')) || [],
                                                    type: 'category',
                                                    axisLabel: {
                                                        color: mode === ModeUI.DARK ? '#aeb7c0' : "#333"
                                                    }
                                                }}
                                                yAxis={{
                                                    min: dataIncome?.findIndex(i => i.total_income - i.total_expense < 0) >= 0 ? undefined : 0,
                                                    axisLabel: {
                                                        color: mode === ModeUI.DARK ? '#aeb7c0' : "#333"
                                                    }
                                                }} 
                                                color={['#91cc75', '#ee6666', '#5470c6']}
                                                legend={{
                                                    top: 0,
                                                    textStyle: {
                                                        color: mode === ModeUI.DARK ? '#aeb7c0' : "#333"
                                                    }
                                                }}
                                                toolbox={{
                                                    feature: {
                                                        magicType: { type: ['line']},
                                                        saveAsImage: {
                                                            name: "report-income-expense",
                                                            backgroundColor: mode === ModeUI.DARK ? '#ffffff' : '#ffffff'
                                                        },
                                                        dataZoom: {},
                                                        restore: {}
                                                    },
                                                }}
                                                isLoading={isFetchingIncome}
                                                grid={{
                                                    backgroundColor: 'transparent',
                                                    show: false
                                                }}
                                                dataZoom={[
                                                    {
                                                      type: 'slider',
                                                      show: true,
                                                      xAxisIndex: [0],
                                                      start: 0,
                                                      end: 100
                                                    }
                                                  ]}
                                                series={[
                                                    {
                                                        data: dataIncome?.map(i => i.total_income.toFixed(2)),
                                                        type: 'line',
                                                        name: t("dashboard.revenue"),
                                                    },
                                                    {
                                                        data: dataIncome?.map(i => i.total_expense.toFixed(2)),
                                                        type: 'line',
                                                        name: t("dashboard.expense"),
                                                    },
                                                    {
                                                        data: dataIncome?.map(i => (i.total_income - i.total_expense).toFixed(2)),
                                                        type: 'line',
                                                        name: t("dashboard.net_income"),
                                                    },
                                                ]}
                                            />
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4 mt-4">
                                <div className="mb-4 mt-6">
                                    <h4 className="text-base font-semibold">{t("dashboard.cashflow")}</h4>
                                    <p>[{t("dashboard.24_months")}]</p>
                                </div>

                                <div className="max-w-full overflow-x-auto mt-3">
                                    <TableComponent
                                        data={[...(dataTotalMonths || [])]?.reverse()}
                                        columns={TableHeaderCash()}
                                        pageSize={20}
                                        isPagination={false}
                                        isLoading={isFetchingTotalMonths}
                                    />
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6 2xl:gap-7.5 mt-3 mb-3">
                                <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                    <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                        <i className="fa-solid fa-house fa-lg text-meta-5" />
                                    </div>
                                    <div className="flex items-center justify-between ms-5">
                                        <div>
                                            <h4 className="text-title-md font-bold text-lg text-meta-5">
                                                {dataSummary ? dataSummary.total_business_units : 0}
                                            </h4>
                                            <span className="text-sm font-medium">{t("dashboard.total_properties")}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                    <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                        <i className="fa-solid fa-door-open fa-lg text-meta-3" />
                                    </div>
                            
                                    <div className="flex items-center justify-between ms-5">
                                        <div>
                                            
                                            <h4 className="text-title-md font-bold text-lg text-meta-3">
                                                {dataSummary ? dataSummary.total_rooms : 0}
                                            </h4>
                                            <p className="text-sm font-medium">{t("dashboard.total_rooms")}</p>
                                            <span className="text-xs font-medium">{`${(dataSummary?.total_rooms || 0) - (dataSummary?.total_vacant_rooms || 0)} ${t("dashboard.occupancy")}`} - {`${dataSummary?.total_vacant_rooms || 0} ${t("dashboard.vacant")}`}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                    <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                        <i className="fa-solid fa-user fa-lg text-yellow-600" />
                                    </div>
                            
                                    <div className="flex items-center justify-between ms-5">
                                        <div>
                                            <h4 className="text-title-md font-bold text-lg text-yellow-600">
                                                {dataSummary ? (100 - dataSummary.total_vacant_rooms / dataSummary?.total_rooms  * 100).toFixed(0) : 0}%
                                            </h4>
                                            <span className="text-sm font-medium">{t("dashboard.occupancy_rate")}</span>
                                        </div>
                                    </div>
                                </div>
                                {/* <div className="flex items-center rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                                    <div className="flex h-13 w-13 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                                        <i className="fa-solid fa-money-check-dollar fa-lg text-red" />
                                    </div>
                            
                                    <div className="flex items-center justify-between ms-5">
                                        <div>
                                            <h4 className="text-title-md font-bold text-lg text-red">
                                                ${dataSummary ? dataSummary.total_income.toLocaleString('en-US', {maximumFractionDigits:2}) : 0}
                                            </h4>
                                            <span className="text-sm font-medium">{t("dashboard.monthly_income")}</span>
                                        </div>
                                    </div>
                                </div> */}
                            </div>

                            <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4 mt-3">
                                <div className="mb-4 mt-6">
                                    <h4 className="text-base font-semibold">{t("dashboard.vacant_rooms")}</h4>
                                </div>
                                <div className="max-w-full overflow-x-auto mt-3">
                                    <TableComponent
                                        data={roomFrees || []}
                                        columns={TableHeaderRoomFree()}
                                        isLoading={isFetchingRoomFree}
                                        titleNone="No vacant rooms"
                                    />
                                </div>
                            </div>
                        </TabPanel>
                    </TabPanels>
                </TabGroup>
            </div>
            

        

            
        </div>
    );
};
